// Requests from guardians: absences and early pickups. Staff mark them done or declined.
import { useState } from "react";
import { Badge, Banner, Button, Select, Table, TextField } from "@vavr/ui";
import { api, dateTime, Link } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { LoadError, Loading, REQUEST_LABEL, Toolbar, today, useNames } from "./common.tsx";

interface Request { id: string; kind: string; child_id: string; by_account_id: string | null; for_date: string; detail: string | null; status: string; created_at: number }

export function Requests() {
  const { base, site, can } = useConsole();
  const [view, setView] = useState<"open" | "date">("open");
  const [date, setDate] = useState(() => today(site.tz));
  const reqs = useGet<{ requests: Request[] }>(view === "open" ? `${base}/requests` : `${base}/requests?date=${date}`);
  const { names } = useNames();
  const act = useAction();
  const set = (r: Request, status: "done" | "declined" | "open") => act.run(() => api("POST", `${base}/requests/${r.id}`, { status }), {
    success: status === "done" ? "Marked done." : status === "declined" ? "Marked declined." : "Reopened.", invalidate: [`${base}/requests`],
  });
  return (
    <div className="pmu-stack">
      <Toolbar>
        <Select label="Show" value={view} onValueChange={(v) => setView(v === "date" ? "date" : "open")} options={[{ value: "open", label: "Open requests" }, { value: "date", label: "All requests for a day" }]} />
        {view === "date" && <TextField label="Day" type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
      </Toolbar>
      {act.error && <Banner tone="danger" title={act.error} />}
      {reqs.loading ? <Loading /> : reqs.error ? <LoadError error={reqs.error} retry={reqs.reload} /> : (
        <Table
          caption="Requests" hideCaption rowKey={(r) => r.id} rows={reqs.data?.requests ?? []} empty={view === "open" ? "No open requests." : "No requests for that day."}
          columns={[
            { key: "for", header: "For", width: "8rem", render: (r) => (r.for_date === today(site.tz) ? <Badge tone="brand">Today</Badge> : r.for_date) },
            { key: "kind", header: "Request", render: (r) => REQUEST_LABEL[r.kind] ?? r.kind },
            { key: "child", header: "Child", rowHeader: true, render: (r) => <Link className="pmu-link" href={`/console/children/${r.child_id}`}>{names.child(r.child_id)}</Link> },
            { key: "detail", header: "Details", render: (r) => r.detail ?? "" },
            { key: "at", header: "Sent", render: (r) => dateTime(r.created_at, site.tz) },
            { key: "status", header: "Status", render: (r) => (r.status === "open" ? <Badge tone="warning">Open</Badge> : r.status === "done" ? <Badge tone="success">Done</Badge> : <Badge>Declined</Badge>) },
            ...(can("presence.write") ? [{
              key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end" as const, render: (r: Request) => r.status === "open" ? (
                <span className="pmu-row pmu-c-row-actions">
                  <Button size="sm" variant="secondary" onClick={() => set(r, "done")}>Done</Button>
                  <Button size="sm" variant="ghost" onClick={() => set(r, "declined")}>Decline</Button>
                </span>
              ) : <Button size="sm" variant="ghost" onClick={() => set(r, "open")}>Reopen</Button>,
            }] : []),
          ]}
        />
      )}
    </div>
  );
}
